import React from "react";

/**
 * SourceChip — a quiet provenance indicator for a content entity.
 *
 * Renders where a page entity (item, person, goal, routine) came from:
 *   - a source document  → "ISP", "Family letter", "Parent interview" (+ date)
 *   - no source          → "Added by hand"
 *
 * `source` is the resolved object from the API (EntitySourceSchema):
 *   { document_id, document_type, label, filename, effective_date, is_interview }
 * or null/undefined when the entity has no source document.
 *
 * Intentionally subtle: this is reference information, not primary content.
 */
export default function SourceChip({ source }) {
  const isManual = !source;
  const label = isManual ? "Added by hand" : source.label;

  // A short date suffix when the source document has an effective date.
  let dateSuffix = "";
  if (!isManual && source.effective_date) {
    const d = new Date(source.effective_date);
    if (!isNaN(d)) {
      dateSuffix = " · " + d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    }
  }

  return (
    <span
      style={{ ...styles.chip, ...(isManual ? styles.manual : styles.sourced) }}
      title={
        isManual
          ? "This was added or is maintained by hand"
          : `From ${source.label}${source.filename ? ` (${source.filename})` : ""}`
      }
    >
      <span style={styles.dot} aria-hidden="true" />
      {label}{dateSuffix}
    </span>
  );
}

const styles = {
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    fontSize: "var(--text-xs)",
    lineHeight: 1.4,
    padding: "1px 8px",
    borderRadius: "var(--radius-pill, 999px)",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  sourced: {
    color: "var(--color-text-secondary)",
    background: "var(--color-bg-subtle)",
    border: "1px solid var(--color-border)",
  },
  manual: {
    color: "var(--color-text-tertiary)",
    background: "transparent",
    border: "1px dashed var(--color-border)",
  },
  dot: {
    width: 5,
    height: 5,
    borderRadius: "50%",
    background: "currentColor",
    opacity: 0.6,
    flexShrink: 0,
  },
};
